import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { OFFER_FLAG } from "@/components/AffiliateOfferModal";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageToggle from "@/components/LanguageToggle";
import PasswordInput from "@/components/PasswordInput";
import { formatApiError, APK_DOWNLOAD_URL } from "@/lib/api";
import { trackCompleteRegistration } from "@/lib/fbPixel";
import { Mic2, Headphones, Loader2, Smartphone, Download } from "lucide-react";
import MetazikIcon from "@/components/MetazikIcon";
import TermsModal from "@/components/legal/TermsModal";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/dda6c0f2-b09b-4c38-b5cb-4457a33da391/images/44ccb18f69f6309f054c8e6a2ce152046e9a2f0e352da81a07b8d5b825d27482.png";

export default function AuthPage() {
  const { t } = useI18n();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  // A shared referral link (/auth?ref=CODE) opens the sign-up form with the code filled in.
  const [params] = useSearchParams();
  const refFromLink = (params.get("ref") || "").trim().toUpperCase();
  const [mode, setMode] = useState(refFromLink ? "signup" : "login"); // login | signup
  const [role, setRole] = useState("viewer");
  const [form, setForm] = useState({ email: "", password: "", name: "", gender: "unspecified", referral_code: refFromLink });
  const [codeCheck, setCodeCheck] = useState(null); // null | {valid, program}

  const checkCode = async () => {
    const code = (form.referral_code || "").trim();
    if (!code) { setCodeCheck(null); return; }
    try {
      const { data } = await api.get(`/affiliate/code/${encodeURIComponent(code)}`);
      setCodeCheck(data);
    } catch { setCodeCheck(null); }
  };
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // Sign-up: the form first opens the Terms of Use; the account is only created on « J'accepte ».
  const [showTerms, setShowTerms] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "signup") setShowTerms(true);
    else send();
  };
  const refuseTerms = () => {
    setShowTerms(false);
    setErr("Sans acceptation des conditions d'utilisation, le compte ne peut pas être créé.");
  };

  const send = async () => {
    setLoading(true);
    try {
      let u;
      if (mode === "login") u = await login(form.email, form.password);
      else {
        const { referral_code, ...rest } = form;
        u = await register({ ...rest, role, accept_terms: true, ...(referral_code.trim() ? { referral_code: referral_code.trim() } : {}) });
        trackCompleteRegistration(role);
        // right after creating the account: present the referral programme once (AffiliateOfferModal)
        try { sessionStorage.setItem(OFFER_FLAG, "1"); } catch {}
      }
      // Admins go straight to the back-office; everyone else to the feed.
      navigate(u?.role === "admin" || u?.role === "staff" ? "/admin" : "/");
    } catch (e) {
      if (!e.response) {
        // No response at all = network error or a request that timed out
        // waiting on a cold-starting backend — not a real rejection, so the
        // raw axios message ("timeout of 25000ms exceeded", "Network Error")
        // must never reach the user as-is.
        setErr("Le serveur met du temps à répondre (il se réveille peut-être). Réessaie dans quelques secondes.");
      } else {
        const raw = formatApiError(e.response.data?.detail) || e.message;
        const dict = t.errors || {};
        setErr(dict[raw] || raw);
      }
    } finally {
      setLoading(false);
      setShowTerms(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {showTerms && (
        <TermsModal
          intro="Pour créer ton compte, lis les conditions d'utilisation et accepte-les."
          busy={loading}
          onAccept={send}
          onRefuse={refuseTerms}
        />
      )}
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/80" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF0050]/10 via-transparent to-[#8A2BE2]/15" />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left side : brand */}
        <div className="hidden lg:flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3" data-testid="auth-logo">
            <MetazikIcon className="w-11 h-11 drop-shadow-[0_0_16px_rgba(255,0,80,0.5)]" />
            <span className="text-3xl font-black font-display text-white">
              Metazik
            </span>
          </Link>
          <div>
            <h1 className="text-5xl xl:text-6xl font-black font-display tracking-tighter leading-[1.05] text-white">
              {t.tagline}
            </h1>
            <p className="mt-6 max-w-md text-zinc-300 text-lg leading-relaxed">{t.auth.sub}</p>
            <div className="mt-10 flex gap-3 flex-wrap">
              <span className="chip bg-white/10 text-white border border-white/10">FEED</span>
              <span className="chip bg-white/10 text-white border border-white/10">STORE</span>
              <span className="chip bg-white/10 text-white border border-white/10">LIBRARY</span>
              <span className="chip bg-white/10 text-white border border-white/10">LIVES</span>
              <span className="chip bg-[#FF0050]/20 text-[#FF0050] border border-[#FF0050]/40">FR · EN</span>
            </div>
            {/* Download CTA — visible without scrolling */}
            <div className="mt-8 flex flex-wrap gap-3" data-testid="landing-download">
              <a
                href={APK_DOWNLOAD_URL}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-white text-black hover:bg-white/90 transition shadow-lg"
                data-testid="landing-apk-btn"
              >
                <Download className="w-4 h-4" /> Télécharger pour Android
              </a>
              <Link
                to="/install"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-white/10 text-white border border-white/15 hover:bg-white/15 transition"
                data-testid="landing-install-btn"
              >
                <Smartphone className="w-4 h-4" /> iPhone et ordinateur : installer
              </Link>
            </div>
          </div>
          <p className="text-xs text-zinc-500">© Metazik — independent music, social by design.</p>
        </div>

        {/* Right side : form */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <div className="flex items-center justify-between mb-6 lg:hidden">
              <Link to="/" className="flex items-center gap-2">
                <MetazikIcon className="w-9 h-9" />
                <span className="text-2xl font-black font-display text-white">
                  Metazik
                </span>
              </Link>
              <div className="flex items-center gap-2">
                <a
                  href={APK_DOWNLOAD_URL}
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold bg-white text-black"
                  data-testid="landing-apk-btn-mobile"
                >
                  <Download className="w-3 h-3" /> APK
                </a>
                <LanguageToggle />
              </div>
            </div>

            <div className="bg-zinc-950/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-8 anim-in">
              <div className="hidden lg:flex justify-end mb-2"><LanguageToggle /></div>
              <h2 className="text-3xl font-black font-display text-white" data-testid="auth-title">
                {t.auth.welcome}
              </h2>
              <p className="text-sm text-zinc-400 mt-1">{t.auth.sub}</p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                {mode === "signup" && (
                  <>
                    <div>
                      <label className="text-xs uppercase tracking-wider font-bold text-zinc-500">{t.auth.role}</label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <RoleCard
                          active={role === "viewer"}
                          onClick={() => setRole("viewer")}
                          icon={<Headphones className="w-5 h-5" />}
                          title={t.auth.roleViewer}
                          desc={t.auth.roleViewerDesc}
                          testid="role-viewer-btn"
                        />
                        <RoleCard
                          active={role === "artist"}
                          onClick={() => setRole("artist")}
                          icon={<Mic2 className="w-5 h-5" />}
                          title={t.auth.roleArtist}
                          desc={t.auth.roleArtistDesc}
                          testid="role-artist-btn"
                        />
                      </div>
                    </div>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={role === "artist" ? t.auth.name : t.auth.pseudo}
                      required
                      className="input-base"
                      data-testid="name-input"
                    />
                    <div>
                      <label className="text-xs uppercase tracking-wider font-bold text-zinc-500">
                        {t.auth.gender || "Genre (optionnel)"}
                      </label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {[
                          { key: "male", label: t.auth.genderMale || "Homme" },
                          { key: "female", label: t.auth.genderFemale || "Femme" },
                          { key: "unspecified", label: t.auth.genderUnspecified || "Préfère ne pas dire" },
                        ].map((g) => (
                          <button
                            key={g.key}
                            type="button"
                            onClick={() => setForm({ ...form, gender: g.key })}
                            data-testid={`gender-${g.key}-btn`}
                            className={`text-center py-2.5 px-1 rounded-2xl border text-xs font-semibold transition ${
                              form.gender === g.key
                                ? "border-[#FF0050] bg-[#FF0050]/10 text-white"
                                : "border-white/10 bg-zinc-900/60 text-zinc-400 hover:border-white/20"
                            }`}
                          >
                            {g.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Code promo (optionnel)</label>
                      <input
                        type="text"
                        value={form.referral_code}
                        onChange={(e) => { setForm({ ...form, referral_code: e.target.value.toUpperCase() }); setCodeCheck(null); }}
                        onBlur={checkCode}
                        placeholder="Ex. MZ7K2PQX"
                        maxLength={32}
                        className="input-base mt-2 tracking-widest"
                        data-testid="referral-code-input"
                      />
                      {codeCheck && (
                        <p className={`text-[11px] mt-1 ${codeCheck.valid ? "text-emerald-400" : "text-red-400"}`} data-testid="referral-code-check">
                          {codeCheck.valid ? `Code valide ✓ (${codeCheck.program})` : "Ce code n'existe pas ou n'est plus actif."}
                        </p>
                      )}
                      <p className="text-[11px] text-zinc-500 mt-1">Il ne pourra plus être ajouté ni changé après l'inscription.</p>
                    </div>
                  </>
                )}
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.auth.email}
                  required
                  className="input-base"
                  data-testid="email-input"
                />
                <PasswordInput
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={t.auth.password}
                  required
                  minLength={6}
                  dataTestid="password-input"
                  ariaShow={t.auth.showPassword}
                  ariaHide={t.auth.hidePassword}
                />

                {err && (
                  <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2" data-testid="auth-error">
                    {err}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full text-base inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="auth-submit"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {mode === "login" ? t.auth.signIn : t.auth.signUp}
                </button>

                {mode === "login" && (
                  <div className="flex items-center justify-between text-xs">
                    <Link to="/forgot-password" className="text-zinc-400 hover:text-white underline" data-testid="forgot-link">Mot de passe oublié ?</Link>
                    <Link to="/support" className="text-zinc-400 hover:text-white underline" data-testid="support-link-auth">Contacter le support</Link>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setMode(mode === "login" ? "signup" : "login")}
                  className="btn-ghost w-full text-sm"
                  data-testid="switch-mode-btn"
                >
                  {mode === "login" ? t.auth.switchToSignup : t.auth.switchToLogin}
                </button>
              </form>

              <div className="mt-4 text-xs text-zinc-500 text-center space-x-3">
                <Link to="/terms" className="hover:text-white underline">Conditions d'utilisation</Link>
                <Link to="/faq" className="hover:text-white underline" data-testid="faq-link-auth">Questions fréquentes</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ active, onClick, icon, title, desc, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-3 rounded-2xl border transition ${active ? "border-[#FF0050] bg-[#FF0050]/10" : "border-white/10 bg-zinc-900/60 hover:border-white/20"}`}
    >
      <div className={`w-9 h-9 grid place-items-center rounded-lg ${active ? "bg-[#FF0050] text-white" : "bg-white/10 text-white"}`}>
        {icon}
      </div>
      <p className="mt-2 font-bold text-white text-sm">{title}</p>
      <p className="text-[11px] text-zinc-400 leading-snug">{desc}</p>
    </button>
  );
}
